/*
 * Aléo Studio "Selections" — guest-session save list, no customer account
 * required. State is an array in localStorage where each entry is EITHER:
 *   - a plain string product handle (an old-style PDP save, from before
 *     this remembered a variant — resolveEntry() below falls back to
 *     that product's first available variant for these), or
 *   - an object { handle, variantId, qty } (current PDP "Save to
 *     Selections": the exact variant selected at the moment of saving,
 *     qty always 1) or a richer { handle, variantId, title, price,
 *     image, options, qty } (the cart drawer's "Save for Later",
 *     snapshotting an existing cart line). entryHandle() normalizes any
 *     shape down to its handle for matching; resolveEntry() normalizes
 *     any shape, plus a live /products/{handle}.js fetch, into a full
 *     { handle, variantId, title, price, image, options, qty, available }
 *     — the one place that knows how to turn a saved entry into a real,
 *     currently-accurate variant. Both the Selections grid page and the
 *     cart drawer's Selections tab (assets/aleo-cart-drawer.js) resolve
 *     every entry through this same function rather than each
 *     implementing their own fallback/lookup.
 *
 * Loaded sitewide (layout/theme.liquid) so the header nav count badge
 * stays correct on every page; the PDP toggle and the Selections grid are
 * only wired up when their markup is actually present. Also re-renders on
 * the theme's real on:cart:add / on:cart:change events, because a real
 * add-to-cart refreshes the header's whole .logo-area__right (see
 * PageHeader.updateFromCartChange in main.js) and would otherwise wipe our
 * badge back to its server-rendered (always-empty) default until the next
 * local mutation — so count badges and the toggle button are re-queried
 * live on every render rather than cached once, since a refresh replaces
 * those DOM nodes outright.
 */
(function () {
  var STORAGE_KEY = 'aleo_selections';

  // Same substring-contains check Liquid uses everywhere else
  // (settings.swatch_option_name contains option.name) -- theme.settings
  // .swatchOptionName is the same raw comma-separated setting string
  // (layout/theme.liquid), so an option is "the color one" exactly when
  // Liquid would also call it that.
  function isColorOptionName(name) {
    var raw = (window.theme && theme.settings && theme.settings.swatchOptionName) || '';
    return !!name && raw.indexOf(name) !== -1;
  }

  function optionValue(options, predicate) {
    if (!options) return null;
    var match = options.filter(predicate)[0];
    return match ? match.value : null;
  }

  /*
   * Bag photo rule, JS side: the Selections tab resolves an entry via a
   * live /products/{handle}.js fetch (below), so it needs its own port
   * of the same color-image algorithm snippets/aleo-color-primary-image
   * .liquid uses server-side for the drawer's Bag tab and the Bag page --
   * majority-rule media grouping (each product image "belongs" to
   * whichever color most of its matching variants' own Variant Image
   * points to it, alt-text substring match as the fallback), then the
   * first image (in product.media order) assigned to that color, or
   * left ungrouped, is the result. Returns null (not a guess) when the
   * product has no color option or the color has no image at all, so
   * the caller can fall through to its own variant/featured-image
   * fallbacks exactly like the Liquid version does.
   */
  function resolveColorImage(product, colorValue) {
    if (!colorValue || !product.media || !product.options) return null;
    var colorPos = -1;
    for (var i = 0; i < product.options.length; i++) {
      if (isColorOptionName(product.options[i].name)) { colorPos = i; break; }
    }
    if (colorPos === -1) return null;
    var colorOptValues = product.options[colorPos].values || [];
    var images = product.media.filter(function (m) { return m.media_type === 'image'; });

    for (var j = 0; j < images.length; j++) {
      var media = images[j];
      var matchedColors = product.variants
        .filter(function (v) { return v.featured_media && v.featured_media.id === media.id; })
        .map(function (v) { return v.options[colorPos]; });

      var mediaColor = '';
      if (matchedColors.length > 0) {
        var counts = {};
        matchedColors.forEach(function (c) { counts[c] = (counts[c] || 0) + 1; });
        var best = '', bestCount = 0;
        Object.keys(counts).forEach(function (c) {
          if (counts[c] > bestCount) { bestCount = counts[c]; best = c; }
        });
        mediaColor = best;
      } else if (media.alt) {
        var altLower = media.alt.toLowerCase();
        for (var k = 0; k < colorOptValues.length; k++) {
          if (altLower.indexOf(colorOptValues[k].toLowerCase()) !== -1) { mediaColor = colorOptValues[k]; break; }
        }
      }

      if (mediaColor === colorValue || mediaColor === '') {
        return media.src || (media.preview_image && media.preview_image.src) || null;
      }
    }
    return null;
  }

  function readSelections() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function writeSelections(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }

  function entryHandle(entry) {
    return typeof entry === 'string' ? entry : entry.handle;
  }

  function entryVariantId(entry) {
    return typeof entry === 'object' && entry.variantId ? Number(entry.variantId) : null;
  }

  /*
   * Identity used for matching/dedup: handle + variant, not handle alone
   * — a product is saved per VARIANT (color and/or size), so the blue
   * attachment and the black attachment are two separate entries, not
   * one overwriting the other. A legacy plain-string entry (saved before
   * this existed) has no variant on record; it keys as handle + '::'
   * same as a rich entry with a null variantId, so it's still treated as
   * its own distinct row rather than silently colliding with a real
   * variant-specific save added later.
   */
  function entryKey(entry) {
    return entryHandle(entry) + '::' + (entryVariantId(entry) || '');
  }

  function isSaved(handle, variantId) {
    var key = handle + '::' + (variantId ? Number(variantId) : '');
    return readSelections().some(function (e) { return entryKey(e) === key; });
  }

  /*
   * Defensive fallback only (no real variant id could be read off the
   * page) — pushes a plain-handle legacy-shape entry. Dedupes against
   * any other entry that also has no variant on record, same as before.
   */
  function addSelection(handle) {
    var list = readSelections();
    if (!list.some(function (e) { return entryKey(e) === handle + '::'; })) {
      list.push(handle);
      writeSelections(list);
    }
    renderAll();
  }

  /*
   * Used by the PDP "Save to Selections" toggle (now carries at least
   * variantId) and the cart drawer's "Save for Later" (carries the full
   * rich shape) — only replaces an existing entry for the exact same
   * variant (e.g. re-saving to refresh qty/snapshot); a different
   * variant of the same product is a separate, additional entry.
   */
  function addSelectionEntry(entry) {
    var key = entryKey(entry);
    var list = readSelections().filter(function (e) { return entryKey(e) !== key; });
    list.push(entry);
    writeSelections(list);
    renderAll();
  }

  function removeSelection(handle, variantId) {
    var key = handle + '::' + (variantId ? Number(variantId) : '');
    writeSelections(readSelections().filter(function (e) { return entryKey(e) !== key; }));
    renderAll();
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  var productCache = {};
  function fetchProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + handle + '.js').then(function (res) { return res.json(); });
    }
    return productCache[handle];
  }

  /*
   * Resolves ANY saved entry shape into one consistent, currently-
   * accurate shape: { handle, variantId, title, price, image, options,
   * qty, available }. Always does a live /products/{handle}.js fetch
   * (cached per handle) rather than trusting a rich entry's own stored
   * variantId/price/availability outright, since those can go stale
   * (price changes, a variant sells out) between saving and viewing —
   * only title/price/image fall back to what was stored, and only when
   * the live product doesn't resolve a variant at all.
   */
  /*
   * Resolves to null (never throws/rejects) for an entry whose product
   * no longer exists or whose live data can't be read -- a deleted or
   * unpublished product, most likely. The caller is responsible for
   * dropping any null result both from what it renders AND from
   * localStorage itself, so a permanently-unresolvable entry doesn't
   * sit there forever inflating the count badge past what's ever
   * actually shown.
   */
  function resolveEntry(entry) {
    var handle = entryHandle(entry);
    var savedVariantId = typeof entry === 'object' ? Number(entry.variantId) : null;

    return fetchProduct(handle).then(function (product) {
      if (!product || !product.variants || !product.variants.length) return null;
      var variant = null;
      if (savedVariantId) {
        variant = product.variants.filter(function (v) { return v.id === savedVariantId; })[0];
      }
      if (!variant) {
        variant = product.variants.filter(function (v) { return v.available; })[0] || product.variants[0];
      }
      var options = product.options.map(function (opt, i) {
        return { name: opt.name, value: variant['option' + (i + 1)] };
      });
      var colorValue = optionValue(options, function (o) { return isColorOptionName(o.name); });
      /*
       * Bag photo rule: the color's primary photo first (must not
       * depend on which size was saved/is selected), not the saved
       * entry's own stored snapshot or this specific variant's image --
       * see resolveColorImage above.
       */
      var image = resolveColorImage(product, colorValue)
        || (variant.featured_image ? variant.featured_image.src : null)
        || product.featured_image
        || (product.images && product.images[0]);

      return {
        handle: handle,
        variantId: variant.id,
        title: (typeof entry === 'object' && entry.title) || product.title,
        price: (typeof entry === 'object' && entry.price) || formatMoney(variant.price),
        image: image,
        options: options,
        qty: (typeof entry === 'object' && entry.qty) || 1,
        available: variant.available
      };
    }).catch(function () {
      return null;
    });
  }

  var grid = null;
  var emptyState = null;
  var clearBtn = null;
  var metaLine = null;
  var tileTemplate = null;

  function renderCountBadges() {
    var badges = document.querySelectorAll('[data-selections-count]');
    if (!badges.length) return;
    var count = readSelections().length;
    badges.forEach(function (badge) {
      badge.textContent = '(' + count + ')';
      badge.hidden = count === 0;
    });
  }

  /*
   * Shopify names the one auto-created option on a single-variant
   * product "Title" with value "Default Title" — never real, merchant-
   * facing data, so it must never reach the option line (or anywhere
   * else; see the other "Default Title" guards sitewide).
   */
  function isRealOptionValue(value) {
    return !!value && value !== 'Default Title';
  }

  function renderToggleButtons() {
    document.querySelectorAll('[data-selections-toggle]').forEach(function (btn) {
      var idInput = document.querySelector('.js-product-form [name="id"]');
      var variantId = idInput && idInput.value ? Number(idInput.value) : null;
      var saved = isSaved(btn.dataset.handle, variantId);
      btn.textContent = saved ? btn.dataset.labelSaved : btn.dataset.labelSave;
      btn.classList.toggle('is-saved', saved);
    });
  }

  /*
   * One tile per saved VARIANT (not per color) -- built fresh from
   * #aleoSelectionsTileTemplate every render rather than toggling
   * hidden on pre-rendered per-color slots, which is what used to leave
   * a second saved size of the same color with nowhere to render even
   * though it was correctly in storage and counted in the header badge.
   * A full rebuild each time is simplest and safe here: this only runs
   * on page load and on an actual save/remove/cart-change, not per
   * frame, and the grid is small (a guest's own saved items).
   *
   * Entries whose product can no longer be resolved (resolveEntry
   * returns null -- a deleted/unpublished product, most likely) are
   * dropped from what's rendered AND written back out of localStorage,
   * so a permanently-broken entry can't sit there forever inflating the
   * count badge past what's ever actually shown.
   */
  function renderSelectionsGrid() {
    if (!grid || !tileTemplate) return;
    var list = readSelections();

    Promise.all(list.map(resolveEntry)).then(function (resolved) {
      var stillValid = [];
      var validEntries = [];
      resolved.forEach(function (entry, i) {
        if (entry) {
          stillValid.push(list[i]);
          validEntries.push(entry);
        }
      });
      if (stillValid.length !== list.length) {
        writeSelections(stillValid);
      }

      grid.querySelectorAll('.aleo-selections-tile').forEach(function (el) { el.remove(); });

      validEntries.forEach(function (entry) {
        var node = tileTemplate.content.firstElementChild.cloneNode(true);
        node.dataset.handle = entry.handle;

        var colorValue = optionValue(entry.options, function (o) { return isColorOptionName(o.name); }) || '';
        var sizeValue = optionValue(entry.options, function (o) { return !isColorOptionName(o.name); });

        var productUrl = '/products/' + entry.handle + '?variant=' + entry.variantId;

        var link = node.querySelector('.aleo-selections-tile__link');
        if (link) link.href = productUrl;
        var metaLink = node.querySelector('.aleo-selections-tile__meta-link');
        if (metaLink) metaLink.href = productUrl;

        var img = node.querySelector('.aleo-selections-tile__img');
        if (img) {
          if (entry.image) {
            img.src = entry.image;
            img.alt = entry.title;
          } else {
            img.remove();
          }
        }

        var nameEl = node.querySelector('.aleo-selections-tile__name');
        if (nameEl) nameEl.textContent = entry.title;
        var priceEl = node.querySelector('.aleo-selections-tile__price');
        if (priceEl) priceEl.textContent = entry.price;

        var optionLine = node.querySelector('.aleo-selections-tile__option-line');
        var parts = [];
        if (isRealOptionValue(colorValue)) parts.push(colorValue);
        if (isRealOptionValue(sizeValue)) parts.push(sizeValue);
        if (optionLine) {
          if (parts.length) {
            optionLine.textContent = parts.join(' · ');
            optionLine.hidden = false;
          } else {
            optionLine.hidden = true;
          }
        }

        var formWrap = node.querySelector('.aleo-selections-tile__form-wrap');
        var soldOut = node.querySelector('.aleo-selections-tile__sold-out');
        if (entry.available) {
          if (formWrap) formWrap.hidden = false;
          if (soldOut) soldOut.hidden = true;
          var idInput = node.querySelector('.js-product-form [name="id"]');
          if (idInput) idInput.value = entry.variantId;
        } else {
          if (formWrap) formWrap.hidden = true;
          if (soldOut) soldOut.hidden = false;
        }

        grid.appendChild(node);
      });

      var count = validEntries.length;
      emptyState.hidden = count > 0;
      grid.hidden = count === 0;
      clearBtn.hidden = count === 0;
      metaLine.textContent = count === 0
        ? 'Selections'
        : 'Selections · ' + count + (count === 1 ? ' item' : ' items');
    });
  }

  function renderAll() {
    renderCountBadges();
    renderToggleButtons();
    renderSelectionsGrid();
  }

  document.addEventListener('DOMContentLoaded', function () {
    grid = document.getElementById('aleoSelectionsGrid');
    emptyState = document.getElementById('aleoSelectionsEmpty');
    clearBtn = document.getElementById('aleoSelectionsClear');
    metaLine = document.getElementById('aleoSelectionsMeta');
    tileTemplate = document.getElementById('aleoSelectionsTileTemplate');

    document.addEventListener('click', function (evt) {
      var toggleBtn = evt.target.closest('[data-selections-toggle]');
      if (!toggleBtn) return;
      var handle = toggleBtn.dataset.handle;
      // Capture whatever variant is currently selected on the page (the
      // same hidden #id input the real Add to Shopping Bag form
      // submits) so the save/remove acts on the exact color/size chosen
      // right now, not just the product as a whole -- a product with
      // another color already saved must stay saved; only THIS variant
      // toggles.
      var idInput = document.querySelector('.js-product-form [name="id"]');
      var variantId = idInput && idInput.value ? Number(idInput.value) : null;
      if (isSaved(handle, variantId)) {
        removeSelection(handle, variantId);
        return;
      }
      if (variantId) {
        addSelectionEntry({ handle: handle, variantId: variantId, qty: 1 });
      } else {
        addSelection(handle);
      }
    });

    // Keep the "Save to Selections" button's saved/unsaved state in sync
    // as the shopper switches color/size on the page -- isSaved() is now
    // variant-specific (see above), so it has to re-check on every real
    // variant change, not just once at load.
    document.addEventListener('on:variant:change', renderToggleButtons);

    /*
     * "Move to Selections" (cart drawer and the full Bag page both use
     * the exact same button/attributes, snippets/cart-item.liquid) --
     * global, not scoped to the drawer, since it now has to work in
     * both places. Snapshots the real cart line from the data-aleo-*
     * attributes snippets/cart-item.liquid already stamps on .cart-item,
     * saves it the same way the product page's own toggle does, then
     * triggers the real .cart-item__remove link's click so the actual
     * cart AJAX removal (handled by <cart-form> itself, assets/main.js)
     * runs completely unmodified -- not a second removal implementation.
     */
    document.addEventListener('click', function (evt) {
      var moveBtn = evt.target.closest('[data-aleo-move-to-selections]');
      if (!moveBtn) return;
      var cartItemEl = moveBtn.closest('.cart-item');
      if (!cartItemEl) return;

      var qtyInput = cartItemEl.querySelector('.cart-item__quantity-input');
      addSelectionEntry({
        handle: cartItemEl.dataset.aleoHandle,
        variantId: Number(cartItemEl.dataset.aleoVariantId),
        title: cartItemEl.dataset.aleoTitle,
        price: cartItemEl.dataset.aleoPrice,
        image: cartItemEl.dataset.aleoImage,
        options: JSON.parse(cartItemEl.dataset.aleoOptions || '[]'),
        qty: qtyInput ? Number(qtyInput.value) : 1
      });

      var removeBtn = cartItemEl.querySelector('.cart-item__remove');
      if (removeBtn) removeBtn.click();
    });

    if (grid) {
      grid.addEventListener('click', function (evt) {
        var removeBtn = evt.target.closest('[data-selections-remove]');
        if (!removeBtn) return;
        evt.preventDefault();
        var tile = removeBtn.closest('.aleo-selections-tile');
        if (!tile) return;
        // Each tile is one exact saved variant now (renderSelectionsGrid
        // builds one tile per entry, not per color) -- its own
        // data-handle and the Add to Bag form's own variant-id input
        // together identify exactly the entry to remove.
        var idInput = tile.querySelector('.js-product-form [name="id"]');
        var variantId = idInput && idInput.value ? Number(idInput.value) : null;
        removeSelection(tile.dataset.handle, variantId);
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        writeSelections([]);
        renderAll();
      });
    }

    document.addEventListener('on:cart:change', renderAll);
    document.addEventListener('on:cart:add', renderAll);

    /*
     * The stock header has TWO separate mechanisms that blindly overwrite
     * server-rendered (always-default, localStorage-blind) markup over our
     * badge well after our own synchronous renderAll() above has already
     * run: PageHeader.updateFromCartChange (assets/main.js), called
     * directly from a real add-to-cart response, replaces
     * `.logo-area__right`'s innerHTML outright -- discarding and
     * recreating `.logo-area__right__inner` as a brand new node; and
     * PageHeader.refreshHeader, triggered on every on:cart:change via its
     * own async fetch to `?sections=header`, later replaces just
     * `.logo-area__right__inner`'s innerHTML in place. Observing
     * `.logo-area__right__inner` directly breaks the first time
     * updateFromCartChange swaps that node out, since the observer stays
     * bound to the old, now-detached node. `.logo-area__right` itself is
     * never replaced wholesale by either path -- only its contents are --
     * so it's the stable anchor to watch.
     */
    var badgeHosts = document.querySelectorAll('.logo-area__right');
    if (badgeHosts.length) {
      var badgeObserver = new MutationObserver(function () {
        badgeObserver.disconnect();
        renderCountBadges();
        badgeHosts.forEach(function (host) {
          badgeObserver.observe(host, { childList: true, subtree: true, characterData: true });
        });
      });
      badgeHosts.forEach(function (host) {
        badgeObserver.observe(host, { childList: true, subtree: true, characterData: true });
      });
    }

    renderAll();
  });

  window.AleoSelections = {
    read: readSelections,
    write: writeSelections,
    entryHandle: entryHandle,
    isSaved: isSaved,
    addSelection: addSelection,
    addSelectionEntry: addSelectionEntry,
    removeSelection: removeSelection,
    resolveEntry: resolveEntry,
    renderAll: renderAll
  };
})();
